import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeftIcon, LockIcon } from 'lucide-react'
import { useState } from 'react'
import { AsortiGrid, type AsortiCell } from '@app/components/asorti-grid'
import { EmirLink, StockLink } from '@app/components/entity-link'
import { ProductImage } from '@app/components/product-image'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@app/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { ApiError } from '@app/lib/api'
import { cn } from '@app/lib/cn'
import type { BomProcess, BomLine, ModelDetail } from '@app/features/modeller/api'
import { fetchModelDetail, modelsKeys } from '@app/features/modeller/api'
import {
  SizeMappingStrip,
  MappingChip,
  ColorMappingList,
} from '@app/features/modeller/mapping'
import { EMIR_STATUS, formatQuantity, formatNumber, formatDate } from '@app/features/modeller/format'
import { SayaCard } from '@app/features/saya/model-card'
import { StatusBlock, ErrorBlock, SkeletonBar } from '@app/features/modeller/states'

const VISIBLE_COLOR_COUNT = 24

export function ModelDetailScreen() {
  const { modelKod } = useParams({ from: '/_korumali/modeller/$modelKod' })
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: modelsKeys.detail(modelKod),
    queryFn: () => fetchModelDetail(modelKod),
  })

  return (
    <>
      <Link
        to="/modeller"
        className="mb-3 inline-flex items-center gap-1.5 text-micro text-ink-muted transition-colors hover:text-kordovan"
      >
        <ArrowLeftIcon className="size-3.5" aria-hidden />
        Modeller
      </Link>

      {isPending ? <DetailSkeleton modelKod={modelKod} /> : null}

      {isError ? (
        <Card>
          {/* 404 is the server's final answer — retrying cannot conjure the
              model up, so the only useful action is the way back to the list. */}
          {error instanceof ApiError && error.status === 404 ? (
            <ErrorBlock
              title="Model bulunamadı"
              message={`${modelKod} kodlu model Korgun'da kayıtlı değil. Listeden doğru modeli seçebilirsiniz.`}
              action={
                <Button asChild variant="primary" size="sm">
                  <Link to="/modeller">Model listesine dön</Link>
                </Button>
              }
            />
          ) : (
            <ErrorBlock
              message={error instanceof Error ? error.message : 'Model bilgisi alınamadı.'}
              onRetry={() => void refetch()}
            />
          )}
        </Card>
      ) : null}

      {data ? <ModelDetailContent model={data} /> : null}
    </>
  )
}

function ModelDetailContent({ model }: { model: ModelDetail }) {
  const { summary } = model

  return (
    <>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        {/*
         * The photo used to be here as its file name in a "Görsel" field — the
         * one piece of a UNC path we could show. Now it is the picture, beside
         * the code it belongs to; dropped below `sm`, where the heading and the
         * meta fields already fill the width.
         */}
        <ProductImage
          imageKey={model.imageKey}
          alt={`${model.name ?? model.modelKod} model fotoğrafı`}
          size="hero"
          className="hidden w-32 sm:block"
        />

        <div className="min-w-0 flex-1">
          <p className="eyebrow">Model kartı</p>
          <h2 className="mt-0.5 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <span className="font-mono text-2xl font-semibold tracking-tight text-ink">
              {model.modelKod}
            </span>
            <span className="font-display text-lg font-medium tracking-tight text-ink-muted">
              {model.name ?? 'Tanım girilmemiş'}
            </span>
            {model.locked ? (
              <Badge tone="warn">
                <LockIcon className="size-3" aria-hidden />
                Kilitli
              </Badge>
            ) : null}
          </h2>
        </div>

        <dl className="flex flex-wrap items-end gap-x-5 gap-y-2">
          <MetaField
            label="Beden serisi"
            value={model.bedKod ?? '—'}
            mono
            hint={
              model.sizes.length > 0
                ? `${model.sizeSeries ?? model.bedKod ?? ''} · ${model.sizes
                    .map((size) => size.size ?? size.bedInx)
                    .join(' ')}`
                : (model.sizeSeries ?? undefined)
            }
          />
          {/* FOran is 0 in every one of the mirror's 581 models — Korgun never had scrap entered. */}
          <MetaField
            label="Fire oranı"
            value={model.foran ? `%${formatQuantity(model.foran)}` : 'girilmemiş'}
            mono={Boolean(model.foran)}
            muted={!model.foran}
            hint="Model_M.FOran — hiçbir model kartında doldurulmamış."
          />
          <MetaField label="Kart tarihi" value={formatDate(model.date)} mono />
        </dl>
      </header>

      <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-hairline bg-hairline sm:grid-cols-3 lg:grid-cols-6">
        <SummaryTile label="Renk" value={summary.colorCount} />
        <SummaryTile
          label="Proses"
          value={summary.processCount}
          note={summary.emptyProcesses.length > 0 ? `${summary.emptyProcesses.length} tanesi boş` : undefined}
        />
        <SummaryTile label="Reçete satırı" value={summary.bomLineCount} />
        <SummaryTile label="Beden eşlemesi" value={summary.sizeMappingCount} />
        <SummaryTile label="Renk eşlemesi" value={summary.colorMappingCount} />
        <SummaryTile label="Üretim emri" value={summary.emirCount} />
      </div>

      <div className="mb-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <ColorsCard model={model} />
        <SizeDistributionCard model={model} />
      </div>

      <section className="mb-4">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-display text-base font-semibold tracking-tight text-ink">
            Reçete — 1 çift için proses bazında malzeme
          </h3>
          <p className="text-micro text-ink-muted">
            Sıra Korgun&apos;daki proses sırasıdır; proses kodu da gösterilir.
          </p>
        </div>

        {model.processes.length === 0 ? (
          <Card>
            <StatusBlock
              title="Bu modelin reçetesi yok"
              description="Model kartı açılmış ama proses ve malzeme satırları henüz girilmemiş."
            />
          </Card>
        ) : (
          <div className="flex flex-col gap-3">
            {model.processes.map((process) => (
              <ProcessCard key={process.proses} process={process} />
            ))}
          </div>
        )}
      </section>

      {/*
       * Saya sits between the recipe and the production history because that is
       * where it belongs in the story the page tells: the recipe says which
       * materials a pair needs, the saya set says what shape they are cut in,
       * and the emirs say what was made of them.
       */}
      <div className="mb-4">
        <SayaCard modelKod={model.modelKod} />
      </div>

      <RecentEmirsCard model={model} />
    </>
  )
}

/* ── Header parts ────────────────────────────────────────────────────────────── */

function MetaField({
  label,
  value,
  mono = false,
  muted = false,
  hint,
}: {
  label: string
  value: string
  mono?: boolean
  muted?: boolean
  hint?: string
}) {
  return (
    <div title={hint}>
      <dt className="eyebrow">{label}</dt>
      <dd
        className={cn(
          'text-data',
          mono && 'font-mono tnum',
          muted ? 'text-ink-muted' : 'text-ink',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

function SummaryTile({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="bg-surface px-3 py-2">
      <p className="eyebrow">{label}</p>
      <p className="font-mono text-lg font-medium tnum text-ink">{formatNumber(value)}</p>
      {note ? <p className="text-micro text-hardal">{note}</p> : null}
    </div>
  )
}

/* ── Colours ─────────────────────────────────────────────────────────────────── */

function ColorsCard({ model }: { model: ModelDetail }) {
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? model.colors : model.colors.slice(0, VISIBLE_COLOR_COUNT)
  const kalan = model.colors.length - visible.length

  return (
    <Card>
      <CardHeader>
        <CardTitle>Renk varyantları</CardTitle>
        <span className="font-mono text-micro tnum text-ink-muted">
          {formatNumber(model.colors.length)} renk
        </span>
      </CardHeader>
      <CardContent>
        {model.colors.length === 0 ? (
          <p className="text-data text-ink-muted">
            Bu modele hiç renk tanımlanmamış. Renkler Korgun&apos;da model kartından eklenir.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {/* The chip carries the colourway's own photo (S_DetPicture under
                the model code), which is the only way a list of colour names
                tells anybody what the model looks like in each of them. */}
            {visible.map((color) => (
              <span
                key={color.rkod}
                title={`Renk kodu ${color.rkod}`}
                className="inline-flex items-center gap-1.5 rounded-xs border border-hairline bg-kagit py-0.5 pl-0.5 pr-2 text-data text-ink"
              >
                <ProductImage
                  imageKey={color.imageKey}
                  alt={`${model.modelKod} · ${color.name ?? `renk ${color.rkod}`} fotoğrafı`}
                  size="line"
                />
                {color.name ?? 'Adsız renk'}
                <span className="font-mono text-micro tnum text-ink-muted">{color.rkod}</span>
              </span>
            ))}
            {kalan > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
                +{formatNumber(kalan)} renk daha
              </Button>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/* ── Size distribution ──────────────────────────────────────────────────────── */

function SizeDistributionCard({ model }: { model: ModelDetail }) {
  // "Completed" = Urtx_fin_gch (actually-finished work); "released to production"
  // is only the pairs pushed out to the shop floor — mixing the two makes a
  // model look done when it isn't.
  const cells: AsortiCell[] = model.sizeDistribution.map((row) => ({
    size: row.size ?? row.bedInx,
    qty: Math.round(row.planned),
    status: row.completed >= row.planned ? 'ok' : 'warn',
  }))
  const planned = model.sizeDistribution.reduce((acc, row) => acc + row.planned, 0)
  const releasedToProduction = model.sizeDistribution.reduce((acc, row) => acc + row.releasedToProduction, 0)
  const completed = model.sizeDistribution.reduce((acc, row) => acc + row.completed, 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Beden dağılımı</CardTitle>
        <span className="text-micro text-ink-muted">Tüm üretim emirleri</span>
      </CardHeader>
      <CardContent>
        {cells.length === 0 ? (
          <p className="text-data text-ink-muted">
            Bu model hiç üretim emrine girmemiş, dolayısıyla beden dağılımı yok.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <AsortiGrid cells={cells} density="sm" total={Math.round(planned)} />
            <p className="text-micro text-ink-muted">
              <span className="font-mono tnum text-ink">{formatNumber(Math.round(completed))}</span> çift
              üretimi tamamlandı ·{' '}
              <span className="font-mono tnum">{formatNumber(Math.round(releasedToProduction))}</span> çift üretime
              verildi ·{' '}
              <span className="font-mono tnum">
                {formatNumber(Math.round(Math.max(0, planned - completed)))}
              </span>{' '}
              çift açık. Sarı sütunlar tamamlananı emre yazılanın altında kalan bedenler.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/* ── Recipe ──────────────────────────────────────────────────────────────────── */

const lineKey = (line: BomLine) => `${line.proses}#${line.partIndex}`

function ProcessCard({ process }: { process: BomProcess }) {
  const [open, setOpen] = useState<Record<string, 'size' | 'color' | undefined>>({})

  const toggle = (line: BomLine, kind: 'size' | 'color') => {
    const key = lineKey(line)
    setOpen((prev) => ({ ...prev, [key]: prev[key] === kind ? undefined : kind }))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-baseline gap-2">
          <span
            className="font-mono text-micro tnum text-ink-muted"
            title="Modelin proses sırası (Model_P.ProsesNo)"
          >
            {process.prosesNo ?? '–'}.
          </span>
          <span>{process.name ?? 'Adsız proses'}</span>
          <span className="font-mono text-micro tnum text-ink-muted" title="Proses kodu">
            {process.proses}
          </span>
        </CardTitle>
        {process.lines.length === 0 ? (
          <Badge tone="warn">Malzeme girilmemiş</Badge>
        ) : (
          <span className="font-mono text-micro tnum text-ink-muted">
            {formatNumber(process.lines.length)} malzeme
          </span>
        )}
      </CardHeader>

      {process.lines.length === 0 ? (
        <CardContent>
          <p className="text-data text-ink-muted">
            Bu proses rotada var ama tek bir malzeme satırı bile girilmemiş — MRP bu prosesten
            hiçbir ihtiyaç üretmez.
          </p>
        </CardContent>
      ) : (
        <TableScroller>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14 text-right">Parça</TableHead>
                {/* Wider than the code needs: the material's photo leads the cell. */}
                <TableHead className="w-52">Malzeme kodu</TableHead>
                <TableHead>Tanım</TableHead>
                <TableHead className="w-24 text-right">Miktar</TableHead>
                <TableHead className="w-16">Birim</TableHead>
                <TableHead className="w-56">Beden / renk eşlemesi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {process.lines.map((line) => (
                <BomLineRow
                  key={lineKey(line)}
                  line={line}
                  open={open[lineKey(line)]}
                  onToggle={(kind) => toggle(line, kind)}
                />
              ))}
            </TableBody>
          </Table>
        </TableScroller>
      )}
    </Card>
  )
}

function BomLineRow({
  line,
  open,
  onToggle,
}: {
  line: BomLine
  open: 'size' | 'color' | undefined
  onToggle: (kind: 'size' | 'color') => void
}) {
  const key = lineKey(line)
  const unitMismatch =
    line.stockUnit != null && line.unit != null && line.stockUnit !== line.unit
  const differentSizeCount = line.sizeMappings.filter((row) => row.different).length
  const differentColorCount = line.colorMappings.filter((row) => row.different).length

  return (
    <>
      <TableRow>
        <TableNumCell className="text-ink-muted">{line.partIndex}</TableNumCell>
        <TableCell>
          <span className="flex items-center gap-1.5">
            {/* Only 48 of the 628 material cards carry a photo, so this is
                mostly the sheet glyph — and the few lines that DO have one are
                the ones worth looking at (uppers, linings). */}
            <ProductImage
              imageKey={line.imageKey}
              alt={`${line.materialName ?? line.skod ?? 'Malzeme'} fotoğrafı`}
              kind="material"
              size="line"
            />
            {/* The recipe's whole point is the material; its stock card answers
                the next question ("do we have any?") without a re-search. */}
            {line.skod ? (
              <StockLink skod={line.skod} className="font-mono font-medium" />
            ) : (
              <span className="font-mono font-medium text-ink">—</span>
            )}
            {line.subModel ? (
              <Link
                to="/modeller/$modelKod"
                params={{ modelKod: line.skod ?? '' }}
                className="text-micro text-kordovan hover:underline"
                title="Bu malzemenin kendi model kartı var"
              >
                alt model
              </Link>
            ) : null}
          </span>
        </TableCell>
        <TableCell className="max-w-72 truncate text-ink-muted" title={line.materialName ?? undefined}>
          {line.materialName ?? line.description ?? '—'}
        </TableCell>
        <TableNumCell className="font-medium text-ink">{formatQuantity(line.quantity)}</TableNumCell>
        <TableCell className="font-mono text-ink-muted">
          <span className="flex items-center gap-1">
            {line.unit ?? '—'}
            {unitMismatch ? (
              <Badge
                tone="warn"
                title={`Stok kartındaki birim ${line.stockUnit}, reçetede ${line.unit}`}
              >
                ≠ {line.stockUnit}
              </Badge>
            ) : null}
          </span>
        </TableCell>
        <TableCell>
          <span className="flex flex-wrap items-center gap-1">
            {line.sizeMappings.length > 0 ? (
              <MappingChip
                label="Beden"
                total={line.sizeMappings.length}
                different={differentSizeCount}
                open={open === 'size'}
                onToggle={() => onToggle('size')}
                controls={`${key}-size`}
              />
            ) : null}
            {line.colorMappings.length > 0 ? (
              <MappingChip
                label="Renk"
                total={line.colorMappings.length}
                different={differentColorCount}
                open={open === 'color'}
                onToggle={() => onToggle('color')}
                controls={`${key}-color`}
              />
            ) : null}
            {line.sizeMappings.length === 0 && line.colorMappings.length === 0 ? (
              <span className="text-micro text-ink-muted/45">eşleme yok</span>
            ) : null}
          </span>
        </TableCell>
      </TableRow>

      {open ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={6} className="h-auto bg-kagit p-0">
            <div id={`${key}-${open}`} className="flex flex-col gap-2 px-3.5 py-3">
              {open === 'size' ? (
                <>
                  <p className="text-micro text-ink-muted">
                    Modelin bedeni hangi malzeme bedenine denk geliyor (Model_B2B).{' '}
                    {differentSizeCount > 0
                      ? `${formatNumber(differentSizeCount)} bedende farklı bir malzeme bedeni çekiliyor.`
                      : 'Tüm bedenler birebir eşleşiyor.'}
                  </p>
                  <SizeMappingStrip rows={line.sizeMappings} />
                </>
              ) : (
                <>
                  <p className="text-micro text-ink-muted">
                    Ayakkabı rengi hangi malzeme rengini çekiyor (Model_R2R).{' '}
                    {differentColorCount > 0
                      ? `${formatNumber(differentColorCount)} renkte malzeme rengi farklı.`
                      : 'Tüm renkler birebir eşleşiyor.'}
                  </p>
                  <ColorMappingList rows={line.colorMappings} />
                </>
              )}
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

/* ── Recent emirs ────────────────────────────────────────────────────────────── */

function RecentEmirsCard({ model }: { model: ModelDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Son üretim emirleri</CardTitle>
        <span className="font-mono text-micro tnum text-ink-muted">
          toplam {formatNumber(model.summary.emirCount)}
        </span>
      </CardHeader>
      {model.recentEmirs.length === 0 ? (
        <CardContent>
          <p className="text-data text-ink-muted">
            Bu model için hiç üretim emri açılmamış.
          </p>
        </CardContent>
      ) : (
        <TableScroller>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24 text-right">Emir no</TableHead>
                <TableHead className="w-32">Emir tarihi</TableHead>
                <TableHead className="w-36">Durum</TableHead>
                <TableHead className="w-24">Lokasyon</TableHead>
                <TableHead>Not</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {model.recentEmirs.map((emir) => {
                const status = EMIR_STATUS[emir.status]
                return (
                  <TableRow key={emir.emirNo}>
                    <TableNumCell className="font-medium">
                      <EmirLink emirNo={emir.emirNo} />
                    </TableNumCell>
                    <TableCell className="font-mono tnum">{formatDate(emir.date)}</TableCell>
                    <TableCell>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-ink-muted">
                      {emir.location ?? '—'}
                    </TableCell>
                    <TableCell className="text-ink-muted">{emir.note ?? '—'}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableScroller>
      )}
    </Card>
  )
}

/* ── Skeleton ────────────────────────────────────────────────────────────────── */

function DetailSkeleton({ modelKod }: { modelKod: string }) {
  return (
    <div aria-busy>
      <p className="eyebrow">Model kartı</p>
      <p className="mt-0.5 font-mono text-2xl font-semibold tracking-tight text-ink">{modelKod}</p>
      <SkeletonBar className="mt-3 w-56" />
      <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-hairline bg-hairline sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="bg-surface px-3 py-3">
            <SkeletonBar className="w-16" />
            <SkeletonBar className="mt-2 w-10" />
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-3">
        {Array.from({ length: 3 }, (_, index) => (
          <Card key={index}>
            <CardContent className="flex flex-col gap-2 py-4">
              <SkeletonBar className="w-40" />
              <SkeletonBar className="w-full" />
              <SkeletonBar className="w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
